import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Cancel,
  DeleteOutline,
  EditOutlined,
  FileDownloadOutlined,
  PersonSearchOutlined,
  SearchRounded,
  UploadFileOutlined,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  createAtendabotProspect,
  deleteAtendabotProspect,
  getAtendabotProspects,
  getUsers,
  importAtendabotProspects,
  updateAtendabotProspect,
} from '../services/api';
import {
  downloadCsvTemplate,
  downloadJsonTemplate,
  parseProspectsFile,
} from '../utils/atendabotProspectsImport';

const PAGE_BG = `
  radial-gradient(ellipse at 0% 0%, rgba(99, 102, 241, 0.07), transparent 45%),
  radial-gradient(ellipse at 100% 0%, rgba(124, 58, 237, 0.05), transparent 45%),
  linear-gradient(180deg, #f7f9ff 0%, #f8fafc 60%, #f7f9ff 100%)
`;

const CARD_SX = {
  borderRadius: '20px',
  border: '1px solid rgba(15, 23, 42, 0.06)',
  boxShadow: '0 14px 36px -14px rgba(15, 23, 42, 0.10), 0 0 0 1px rgba(15, 23, 42, 0.02)',
  background: '#ffffff',
  overflow: 'hidden',
};

const SECTION_ICON_BOX_SX = {
  width: 40,
  height: 40,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)',
  color: '#6366f1',
  flexShrink: 0,
  border: '1px solid rgba(99,102,241,0.18)',
};

const EMPTY_FORM = { name: '', phone: '', information: '', nicho: '', user: '' };

const formatUserOption = (user) => {
  if (!user) return '';
  const displayName = String(user.nome || user.username || 'Sem nome').trim();
  return `${user.id} - ${displayName}`;
};

const formatDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('pt-BR');
  } catch (_err) {
    return '-';
  }
};

const truncate = (value, max = 60) => {
  const text = String(value || '').trim();
  if (!text) return '-';
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
};

const ProspectFormDialog = ({ open, initial, adminUsers = [], onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: initial?.name || '',
      phone: initial?.phone || '',
      information: initial?.information || '',
      nicho: initial?.nicho || '',
      user: initial?.user != null ? String(initial.user) : '',
    });
  }, [open, initial]);

  const handleSave = async () => {
    if (!String(form.phone || '').trim()) {
      toast.error('Telefone é obrigatório.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        information: form.information.trim(),
        nicho: form.nicho.trim(),
        user: form.user ? Number(form.user) : null,
      };
      if (initial?.id) {
        await updateAtendabotProspect(initial.id, payload);
        toast.success('Prospect atualizado.');
      } else {
        await createAtendabotProspect(payload);
        toast.success('Prospect criado.');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || 'Erro ao salvar prospect.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initial?.id ? 'Editar prospect' : 'Novo prospect'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Nome"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Telefone"
            value={form.phone}
            onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
            fullWidth
            required
            helperText="Com DDI 55 quando possível (ex.: 5551999887766)"
          />
          <TextField
            label="Nicho"
            value={form.nicho}
            onChange={(e) => setForm((prev) => ({ ...prev, nicho: e.target.value }))}
            fullWidth
          />
          <TextField
            select
            label="Usuário"
            value={form.user}
            onChange={(e) => setForm((prev) => ({ ...prev, user: e.target.value }))}
            fullWidth
            helperText="Somente usuários admin"
          >
            <MenuItem value="">Nenhum</MenuItem>
            {form.user && !adminUsers.some((user) => String(user.id) === String(form.user)) && (
              <MenuItem value={form.user}>{form.user}</MenuItem>
            )}
            {adminUsers.map((user) => (
              <MenuItem key={user.id} value={String(user.id)}>
                {formatUserOption(user)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Informações adicionais"
            value={form.information}
            onChange={(e) => setForm((prev) => ({ ...prev, information: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: '#6366f1' }}>
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ImportDialog = ({ open, onClose, onImported }) => {
  const [items, setItems] = useState([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [parseError, setParseError] = useState('');

  useEffect(() => {
    if (!open) return;
    setItems([]);
    setFileName('');
    setResult(null);
    setParseError('');
  }, [open]);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setParseError('');
    setResult(null);
    try {
      const parsed = await parseProspectsFile(file);
      setItems(parsed);
      setFileName(file.name);
    } catch (err) {
      setItems([]);
      setFileName('');
      setParseError(err?.message || 'Falha ao ler arquivo.');
    }
  };

  const handleImport = async () => {
    if (!items.length) {
      toast.error('Nenhum item para importar.');
      return;
    }

    setImporting(true);
    try {
      const data = await importAtendabotProspects(items.map((item) => ({
        ...item,
        user: item.user === '' ? null : Number(item.user),
      })));
      setResult(data);
      toast.success(`Importação concluída: ${data.created} criados, ${data.updated} atualizados.`);
      onImported();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || 'Erro ao importar.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Importar prospects</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Envie um CSV ou JSON no formato do template. Telefones existentes serão atualizados (upsert).
            A coluna <strong>user</strong> deve ser o ID de um usuário admin.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlined />}
              onClick={downloadCsvTemplate}
            >
              Template CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlined />}
              onClick={downloadJsonTemplate}
            >
              Template JSON
            </Button>
          </Stack>

          <Button
            variant="contained"
            component="label"
            startIcon={<UploadFileOutlined />}
            sx={{ bgcolor: '#6366f1', alignSelf: 'flex-start' }}
          >
            Selecionar arquivo
            <input hidden type="file" accept=".csv,.json,text/csv,application/json" onChange={handleFile} />
          </Button>

          {fileName && (
            <Typography variant="body2">
              Arquivo: <strong>{fileName}</strong> — {items.length} registro(s)
            </Typography>
          )}

          {parseError && <Alert severity="error">{parseError}</Alert>}

          {result && (
            <Alert severity="info">
              Criados: {result.created} · Atualizados: {result.updated} · Ignorados: {result.skipped}
              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {result.errors.slice(0, 5).map((err) => (
                    <li key={`${err.index}-${err.phone}`}>
                      Linha {err.index + 1}{err.phone ? ` (${err.phone})` : ''}: {err.message}
                    </li>
                  ))}
                  {result.errors.length > 5 && (
                    <li>…e mais {result.errors.length - 5} erro(s)</li>
                  )}
                </Box>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={importing}>Fechar</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={importing || !items.length}
          sx={{ bgcolor: '#6366f1' }}
        >
          {importing ? 'Importando…' : 'Importar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ProspectsTable = ({
  items,
  usersById,
  onEdit,
  onDelete,
  count,
  page,
  pageSize,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const headCells = [
    { id: 'name', label: 'Nome', align: 'left', width: '16%' },
    { id: 'phone', label: 'Telefone', align: 'left', width: '14%' },
    { id: 'nicho', label: 'Nicho', align: 'left', width: '12%' },
    { id: 'user', label: 'Usuário', align: 'left', width: '16%' },
    { id: 'information', label: 'Informações', align: 'left', width: '18%' },
    { id: 'msg_sent', label: 'Msg enviada', align: 'left', width: '10%' },
    { id: 'created_at', label: 'Criado em', align: 'left', width: '14%' },
    { id: 'acoes', label: 'Ações', align: 'right', width: '8%' },
  ];

  const userLabel = (userId) => {
    if (userId == null || userId === '') return '-';
    const user = usersById[String(userId)];
    return user ? formatUserOption(user) : String(userId);
  };

  return (
    <Box sx={CARD_SX}>
      <TableContainer sx={{ px: { xs: 1, md: 1.5 }, pt: 1.5, pb: 0 }}>
        <Table sx={{ minWidth: 1080, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              {headCells.map((hc) => (
                <TableCell
                  key={hc.id}
                  align={hc.align}
                  sx={{
                    width: hc.width,
                    borderBottom: '1px solid #eaecf0',
                    py: 1.75,
                    px: 2.25,
                    fontSize: '0.775rem',
                    fontWeight: 700,
                    color: '#475467',
                    letterSpacing: '0.01em',
                  }}
                >
                  {hc.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headCells.length} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                  Nenhum prospect encontrado.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell sx={{ px: 2.25, py: 1.75 }}>
                    <Typography sx={{ fontWeight: 600, color: '#0f172a' }}>
                      {item.name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ px: 2.25, py: 1.75 }}>{item.phone || '-'}</TableCell>
                  <TableCell sx={{ px: 2.25, py: 1.75 }}>{item.nicho || '-'}</TableCell>
                  <TableCell sx={{ px: 2.25, py: 1.75 }}>{userLabel(item.user)}</TableCell>
                  <TableCell sx={{ px: 2.25, py: 1.75 }}>
                    <Tooltip title={item.information || ''} disableHoverListener={!item.information}>
                      <Typography variant="body2" color="text.secondary">
                        {truncate(item.information, 40)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ px: 2.25, py: 1.75 }}>
                    <Chip
                      size="small"
                      icon={item.msg_sent ? <CheckCircle /> : <Cancel />}
                      label={item.msg_sent ? 'Sim' : 'Não'}
                      color={item.msg_sent ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ px: 2.25, py: 1.75 }}>{formatDate(item.created_at)}</TableCell>
                  <TableCell align="right" sx={{ px: 1.5, py: 1.25 }}>
                    <IconButton size="small" onClick={() => onEdit(item)} aria-label="Editar">
                      <EditOutlined fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => onDelete(item.id)} aria-label="Excluir" color="error">
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={pageSize}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="Por página"
      />
    </Box>
  );
};

const AtendabotProspectsPage = () => {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);

  const usersById = adminUsers.reduce((acc, user) => {
    acc[String(user.id)] = user;
    return acc;
  }, {});

  const load = useCallback(async () => {
    try {
      const res = await getAtendabotProspects({
        page: page + 1,
        pageSize,
        search,
      });
      setItems(res.data || []);
      setCount(res.count || 0);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Erro ao carregar prospects.');
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    const loadAdmins = async () => {
      try {
        const users = await getUsers();
        const list = Array.isArray(users) ? users : [];
        setAdminUsers(list.filter((user) => user?.is_admin === true));
      } catch (err) {
        console.error(err);
        toast.error('Erro ao carregar usuários admin.');
      }
    };
    loadAdmins();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleEdit = (item) => {
    setEditing(item);
    setFormOpen(true);
  };

  const handleNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este prospect?')) return;
    try {
      await deleteAtendabotProspect(id);
      toast.success('Prospect excluído.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao excluir prospect.');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: PAGE_BG, py: { xs: 3, md: 4 } }}>
      <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>
          <Box sx={CARD_SX}>
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 60%, #a78bfa 100%)' }} />
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                gap={2}
                mb={2.5}
              >
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Box sx={SECTION_ICON_BOX_SX}>
                    <PersonSearchOutlined sx={{ fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography
                      fontWeight={800}
                      sx={{
                        color: '#0f172a',
                        fontSize: { xs: '1.25rem', md: '1.4rem' },
                        letterSpacing: '-0.02em',
                        lineHeight: 1.2,
                        mb: 0.25,
                      }}
                    >
                      Prospects Atendabot
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Cadastre e importe números autorizados quando only_prospects estiver ativo.
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1} flexShrink={0}>
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileOutlined />}
                    onClick={() => setImportOpen(true)}
                    sx={{ borderRadius: '12px', fontWeight: 700 }}
                  >
                    Importar
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleNew}
                    sx={{
                      borderRadius: '12px',
                      px: 2.5,
                      py: 1,
                      fontWeight: 700,
                      boxShadow: 'none',
                      bgcolor: '#6366f1',
                      '&:hover': { bgcolor: '#4f46e5', boxShadow: '0 4px 14px rgba(99,102,241,0.3)' },
                    }}
                  >
                    Novo Prospect
                  </Button>
                </Stack>
              </Stack>

              <Divider sx={{ mb: 2.5, borderColor: 'rgba(15,23,42,0.06)' }} />

              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontSize: { xs: '1.6rem', md: '2rem' },
                    fontWeight: 800,
                    color: '#0f172a',
                    lineHeight: 1.15,
                    mb: 0.25,
                  }}
                >
                  {count}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: '#94a3b8',
                  }}
                >
                  {search ? 'Prospects encontrados' : 'Prospects cadastrados'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ ...CARD_SX, overflow: 'visible' }}>
            <Box sx={{ p: { xs: 1.75, md: 2.25 } }}>
              <TextField
                label="Buscar"
                size="small"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: '100%',
                  maxWidth: 420,
                  '& .MuiOutlinedInput-root': { height: 42, bgcolor: '#fff', borderRadius: '12px' },
                }}
              />
            </Box>
          </Box>

          <ProspectsTable
            items={items}
            usersById={usersById}
            onEdit={handleEdit}
            onDelete={handleDelete}
            count={count}
            page={page}
            pageSize={pageSize}
            onPageChange={(_e, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Stack>
      </Box>

      <ProspectFormDialog
        open={formOpen}
        initial={editing}
        adminUsers={adminUsers}
        onClose={() => setFormOpen(false)}
        onSaved={load}
      />
      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={load}
      />
    </Box>
  );
};

export default AtendabotProspectsPage;
